// A deliberately narrow, safe alternative to a full markdown renderer for
// free-text fields an organiser types into a plain textarea (currently just
// the public event description). Turns [label](url) and bare http(s)/mailto
// URLs into link tokens a component can render as real <a> elements -- never
// via dangerouslySetInnerHTML, so an organiser can only ever produce a link,
// never arbitrary HTML or a script tag. Anything else (javascript:, data:, a
// literal "[x](y)" with an unsafe scheme) is left as plain text, exactly as
// typed, because the pattern below only ever matches an http(s)/mailto URL.
const TOKEN_PATTERN =
  /\[([^\]\n]+)\]\(((?:https?:|mailto:)[^\s)]+)\)|\b((?:https?:\/\/|mailto:)[^\s<>()]+)/gi;

// Trailing punctuation that's almost always sentence punctuation, not part
// of the URL itself, when the organiser just pasted a bare link inline.
// [label](url) links are never trimmed -- the parentheses already delimit
// the URL exactly.
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

export function parseRichText(text) {
  const input = text || "";
  const tokens = [];
  let lastIndex = 0;
  let match;
  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(input)) !== null) {
    const [full, mdLabel, mdUrl, bareUrl] = match;
    if (match.index > lastIndex) {
      tokens.push({ type: "text", value: input.slice(lastIndex, match.index) });
    }

    if (mdUrl) {
      tokens.push({ type: "link", label: mdLabel, url: mdUrl });
    } else {
      let url = bareUrl;
      let trailing = "";
      const trailingMatch = url.match(TRAILING_PUNCTUATION);
      if (trailingMatch) {
        trailing = trailingMatch[0];
        url = url.slice(0, -trailing.length);
      }
      if (url) {
        tokens.push({ type: "link", label: url, url });
      }
      if (trailing) {
        tokens.push({ type: "text", value: trailing });
      }
    }

    lastIndex = match.index + full.length;
  }
  if (lastIndex < input.length) {
    tokens.push({ type: "text", value: input.slice(lastIndex) });
  }
  return mergeAdjacentTextTokens(tokens);
}

// The trailing-punctuation split above can leave two text tokens back to
// back (the stripped punctuation, then the untouched rest of the string) --
// collapse those into one so a caller/test sees exactly what a reader does:
// one run of plain text between two links, not an arbitrary split.
function mergeAdjacentTextTokens(tokens) {
  const merged = [];
  for (const token of tokens) {
    const prev = merged[merged.length - 1];
    if (token.type === "text" && prev && prev.type === "text") {
      prev.value += token.value;
    } else {
      merged.push({ ...token });
    }
  }
  return merged;
}
